import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
  styled,
  LinearProgress,
  IconButton,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  PriorityHigh as PriorityIcon,
  Verified as VerifiedIcon,
  ArrowCircleUp as ArrowUpIcon,
  ArrowCircleDown as ArrowDownIcon,
  QrCode as QrCodeIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { tokenWalletService } from '../../../services/skillcoinService';
import { useEventBus } from '../../../hooks/useEventBus';

// Custom styled components
const GlassCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius * 3,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
  backdropFilter: 'blur(10px)',
  color: theme.palette.primary.contrastText,
  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 25px 50px ${alpha(theme.palette.primary.main, 0.4)}`
  }
}));

const CardPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  opacity: 0.05,
  backgroundImage: `radial-gradient(circle at 50px 50px, ${theme.palette.common.white} 1px, transparent 0), 
                    radial-gradient(circle at 150px 150px, ${theme.palette.common.white} 1px, transparent 0),
                    radial-gradient(circle at 250px 250px, ${theme.palette.common.white} 1px, transparent 0),
                    radial-gradient(circle at 350px 350px, ${theme.palette.common.white} 1px, transparent 0)`,
  backgroundSize: '50px 50px',
  pointerEvents: 'none',
  zIndex: 0
}));

const CircleDecoration = styled(Box)(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.common.white, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const GlowingChip = styled(Chip)(({ theme, glowColor }) => ({
  backgroundColor: alpha(glowColor || theme.palette.success.main, 0.2),
  color: glowColor || theme.palette.success.main,
  fontWeight: 'bold',
  boxShadow: `0 0 15px ${alpha(glowColor || theme.palette.success.main, 0.5)}`,
  '& .MuiChip-icon': {
    color: glowColor || theme.palette.success.main
  }
}));

const CardDivider = styled(Divider)(({ theme }) => ({
  borderColor: alpha(theme.palette.common.white, 0.2),
  margin: theme.spacing(2, 0)
}));

const StatsLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: alpha(theme.palette.common.white, 0.7),
  marginBottom: theme.spacing(0.5),
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
}));

const StatsValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: theme.palette.common.white,
  fontFamily: "'Roboto Mono', monospace",
}));

const TokenAddressChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  color: theme.palette.common.white,
  fontFamily: "'Roboto Mono', monospace",
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: theme.palette.common.white
  }
}));

const TokenWalletCard = ({ wallet, loading = false, onRefresh }) => {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState({
    totalEarned: 0,
    totalSpent: 0, 
    totalSent: 0,
    availableBalance: 0
  });
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [walletData, setWalletData] = useState(wallet);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const eventBus = useEventBus();
  
  // Update local wallet data when prop changes
  useEffect(() => {
    setWalletData(wallet);
    if (wallet && wallet._id) {
      calculateWalletStats(wallet);
    }
  }, [wallet]);
  
  // Calculate wallet statistics from wallet data and transactions
  const calculateWalletStats = useCallback((walletData, txData = null) => {
    if (!walletData) return;
    
    // Use provided transaction data or existing state
    const transactionData = txData || transactions;
    
    // Calculate totals based on transaction types
    let totalEarned = 0;
    let totalSpent = 0;
    let totalSent = 0;
    
    if (transactionData && transactionData.length > 0) {
      // Process transactions to calculate totals
      transactionData.forEach(tx => {
        const amount = parseFloat(tx.amount) || 0;
        
        // Enhanced logic for categorizing transactions
        if (tx.type === 'earn' || tx.type === 'credit' || tx.type === 'transfer_in' || 
            (tx.transactionType === 'credit')) {
          totalEarned += amount;
        } else if (tx.type === 'spend' || 
                  (tx.type === 'debit' && 
                  !tx.description?.toLowerCase().includes('transfer'))) {
          totalSpent += amount;
        } else if (tx.type === 'transfer_out' || 
                  (tx.type === 'debit' && 
                  tx.description?.toLowerCase().includes('transfer'))) {
          totalSent += amount;
        }
      });
    } else {
      // Fall back to wallet data if no transactions
      totalEarned = parseFloat(walletData.totalEarned || walletData.earned || 0);
      totalSpent = parseFloat(walletData.totalSpent || walletData.spent || 0);
      totalSent = parseFloat(walletData.totalSent || walletData.sent || 0);
    }
    
    // Ensure we're working with numbers
    totalEarned = isNaN(totalEarned) ? 0 : totalEarned;
    totalSpent = isNaN(totalSpent) ? 0 : totalSpent;
    totalSent = isNaN(totalSent) ? 0 : totalSent;
    
    // Calculate available balance as earned - (spent + sent)
    const availableBalance = totalEarned - totalSpent - totalSent;
    
    console.log('Calculated wallet stats:', {
      totalEarned,
      totalSpent,
      totalSent,
      availableBalance
    });
    
    // Update the transaction stats state
    setTransactionStats({
      totalEarned,
      totalSpent,
      totalSent,
      availableBalance
    });
    
    return {
      totalEarned,
      totalSpent,
      totalSent,
      availableBalance
    };
  }, [transactions]);

  // Function to refresh wallet data completely
  const refreshWalletData = useCallback(async () => {
    if (!wallet || !wallet._id) {
      showNotification('No wallet ID available for refresh', 'error');
      return;
    }
    
    setRefreshing(true);
    try {
      console.log('Refreshing wallet data for ID:', wallet._id);
      
      // Add a random timestamp to prevent caching
      const timestamp = Date.now() + Math.random();
      
      // First refresh the wallet itself to get updated balance
      const walletResponse = await tokenWalletService.getWallet(wallet._id, timestamp);
      if (!walletResponse.success) {
        throw new Error(walletResponse.error || 'Failed to refresh wallet data');
      }
      
      const freshWalletData = walletResponse.data;
      console.log('Fresh wallet data received:', freshWalletData);
      
      // Then refresh transactions with a different timestamp
      const txResponse = await tokenWalletService.getTransactions(wallet._id, { 
        limit: 500,
        cacheBust: Date.now() + Math.random()
      });
      
      if (!txResponse.success) {
        throw new Error(txResponse.error || 'Failed to refresh transaction data');
      }
      
      const freshTransactions = txResponse.data;
      console.log(`Received ${freshTransactions.length} transactions`);
      
      // Update state with new data
      setWalletData(freshWalletData);
      setTransactions(freshTransactions);
      
      // Calculate updated stats
      const stats = calculateWalletStats(freshWalletData, freshTransactions);
      
      // Call parent refresh if available
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh(freshWalletData);
      }
      
      showNotification('Wallet data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing wallet data:', error);
      showNotification(error.message || 'Error refreshing wallet data', 'error');
    } finally {
      setRefreshing(false);
    }
  }, [wallet, onRefresh, calculateWalletStats]);
  
  const showNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  // Listen for token transaction events
  useEffect(() => {
    const handleTransactionEvent = (data) => {
      console.log('Transaction event detected:', data);
      refreshWalletData();
    };
    
    // Subscribe to all relevant transaction events
    eventBus.on('token.transaction.created', handleTransactionEvent);
    eventBus.on('token.transaction.completed', handleTransactionEvent);
    eventBus.on('wallet.updated', handleTransactionEvent);
    eventBus.on('token.spend.completed', handleTransactionEvent);
    eventBus.on('token.spend.success', handleTransactionEvent);
    eventBus.on('token.transfer.completed', handleTransactionEvent);
    eventBus.on('token.balance.updated', handleTransactionEvent);
    
    return () => {
      // Cleanup subscriptions
      eventBus.off('token.transaction.created', handleTransactionEvent);
      eventBus.off('token.transaction.completed', handleTransactionEvent);
      eventBus.off('wallet.updated', handleTransactionEvent);
      eventBus.off('token.spend.completed', handleTransactionEvent);
      eventBus.off('token.spend.success', handleTransactionEvent);
      eventBus.off('token.transfer.completed', handleTransactionEvent);
      eventBus.off('token.balance.updated', handleTransactionEvent);
    };
  }, [wallet, eventBus, refreshWalletData]);
  
  // Fetch transactions when component mounts or wallet changes
  useEffect(() => {
    if (wallet && wallet._id) {
      setIsLoadingTransactions(true);
      
      // Fetch all transactions for this wallet
      tokenWalletService.getTransactions(wallet._id, { 
        limit: 500,
        cacheBust: Date.now() // Add cache busting
      })
        .then(response => {
          if (response.success && response.data) {
            const txData = response.data;
            setTransactions(txData);
            calculateWalletStats(wallet, txData);
          } else {
            console.error('Error in transaction response:', response.error);
          }
        })
        .catch(error => {
          console.error('Error fetching wallet transactions:', error);
        })
        .finally(() => {
          setIsLoadingTransactions(false);
        });
    }
  }, [wallet, calculateWalletStats]);
  
  // Add a more frequent auto-refresh timer
  useEffect(() => {
    // Initial fetch after component mounts
    if (wallet && wallet._id) {
      refreshWalletData();
    }
    
    // Set up an auto-refresh interval
    const refreshInterval = setInterval(() => {
      if (wallet && wallet._id) {
        console.log('Auto-refreshing wallet data...');
        refreshWalletData();
      }
    }, 60000); // Refresh every minute
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [wallet, refreshWalletData]);
  
  if (loading || isLoadingTransactions) {
    return (
      <GlassCard 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress 
            size={60} 
            thickness={4} 
            sx={{ 
              color: alpha(theme.palette.common.white, 0.9)
            }} 
          />
        </CardContent>
      </GlassCard>
    );
  }

  if (!walletData) {
    return (
      <GlassCard
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.2),
                p: 1,
                mr: 2,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.25)}`
              }}
            >
              <WalletIcon />
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              Wallet Not Available
            </Typography>
          </Box>
          
          <Box 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              bgcolor: alpha(theme.palette.common.white, 0.05),
              borderRadius: 2
            }}
          >
            <PriorityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
            <Typography variant="body1" paragraph>
              No wallet information is available at this time.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Please contact support if this issue persists.
            </Typography>
          </Box>
        </CardContent>
      </GlassCard>
    );
  }

  // Token information
  const tokenName = walletData.token?.name || 'MembaCoin';
  const tokenSymbol = walletData.token?.symbol || 'MBC';
  const tokenAddress = walletData.token?.address || walletData.id || 'SC' + Math.random().toString(36).substring(2, 10).toUpperCase();
  const isActive = walletData.status === 'active';
  const baseRate = walletData.token?.baseRate || 0;
  
  // Use calculated stats for display
  const { availableBalance, totalEarned, totalSpent, totalSent } = transactionStats;
  
  // Calculate percentage of growth
  const growthPercentage = totalEarned > 0 ? (availableBalance / totalEarned) * 100 : 0;
  
  // Format balance for display
  const formatBalance = (balance) => {
    if (balance >= 1000000) {
      return (balance / 1000000).toFixed(2) + 'M';
    } else if (balance >= 1000) {
      return (balance / 1000).toFixed(2) + 'K';
    } else {
      return balance.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  };

  // Copy address to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('Token address copied to clipboard');
  };

  return (
    <GlassCard
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <CardPattern />
      <CircleDecoration size={150} top="-50px" right="-50px" opacity={0.1} />
      <CircleDecoration size={100} bottom="-20px" left="20%" opacity={0.08} />
      
      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <motion.div
              animate={{ rotateY: [0, 360] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              style={{ marginRight: theme.spacing(1.5) }}
            >
              <Avatar 
                src={walletData.token?.logoUrl || '/token-logo.png'} 
                alt={tokenName}
                sx={{ 
                  width: 42, 
                  height: 42,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.25)}`
                }}
              >
                <WalletIcon />
              </Avatar>
            </motion.div>
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {tokenName} Wallet
              </Typography>
              <TokenAddressChip
                label={tokenAddress.substring(0, 10) + '...'}
                size="small"
                deleteIcon={<CopyIcon />}
                onDelete={() => copyToClipboard(tokenAddress)}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Refresh wallet data">
              <IconButton 
                size="small" 
                onClick={refreshWalletData}
                disabled={refreshing}
                sx={{ 
                  mr: 1,
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  color: theme.palette.common.white,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                  }
                }}
              >
                {refreshing ? 
                  <CircularProgress size={16} thickness={4} sx={{ color: alpha(theme.palette.common.white, 0.9) }} /> : 
                  <RefreshIcon fontSize="small" />
                }
              </IconButton>
            </Tooltip>
            
            <GlowingChip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              icon={isActive ? <VerifiedIcon /> : <PriorityIcon />}
              glowColor={isActive ? theme.palette.success.main : theme.palette.error.main}
            />
          </Box>
        </Box>
        
        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: alpha(theme.palette.common.white, 0.1),
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`
          }}
        >
          <StatsLabel>Available Balance</StatsLabel>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <motion.div
              key={availableBalance} // Re-run animation when balance changes
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography 
                variant="h3" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  background: `linear-gradient(90deg, ${theme.palette.common.white}, ${alpha(theme.palette.common.white, 0.7)})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  fontFamily: "'Roboto Mono', monospace",
                }}
              >
                {formatBalance(availableBalance)}
              </Typography>
            </motion.div>
            
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.7,
                fontWeight: 'bold',
                ml: 1,
                fontFamily: "'Roboto Mono', monospace",
              }}
            >
              {tokenSymbol}
            </Typography>
          </Box>
          
          {baseRate > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                ≈ ${(availableBalance * baseRate).toLocaleString(undefined, { maximumFractionDigits: 2 })} USD
              </Typography>
              <Tooltip title="Approximate USD value based on token rate">
                <InfoIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
              </Tooltip>
            </Box>
          )}
        </Box>
        
        <CardDivider />
        
        <Box sx={{ mb: 2 }}>
          <StatsLabel>Token Activity</StatsLabel>
          
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.common.white, 0.1),
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArrowDownIcon sx={{ color: theme.palette.success.main, mr: 0.5 }} />
                  <StatsValue>{formatBalance(totalEarned)}</StatsValue>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>Earned</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArrowUpIcon sx={{ color: theme.palette.error.main, mr: 0.5 }} />
                  <StatsValue>{formatBalance(totalSpent)}</StatsValue>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>Spent</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SendIcon sx={{ color: theme.palette.warning.main, mr: 0.5 }} />
                  <StatsValue>{formatBalance(totalSent)}</StatsValue>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>Sent</Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Transaction Count: {transactions.length}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Net Growth: {growthPercentage.toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 8, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: 4, overflow: 'hidden' }}>
              <Box 
                component={motion.div}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(0, Math.min(100, growthPercentage))}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                sx={{ 
                  height: '100%', 
                  background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                  borderRadius: 4,
                  boxShadow: `0 0 10px ${alpha(theme.palette.success.main, 0.7)}`
                }} 
              />
            </Box>
          </Box>
        </Box>
        
        <CardDivider />
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {walletData.settings?.allowPeerTransfer ? (
            <GlowingChip
              label="Transferable"
              size="small"
              glowColor={theme.palette.info.main}
              sx={{ fontWeight: 'medium' }}
            />
          ) : (
            <Chip
              label="Non-transferable"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.common.white, 0.1), 
                color: theme.palette.common.white, 
                opacity: 0.6 
              }}
            />
          )}

          {walletData.settings?.allowExternalExchange ? (
            <GlowingChip
              label="Exchangeable"
              size="small"
              glowColor={theme.palette.warning.main}
              sx={{ fontWeight: 'medium' }}
            />
          ) : (
            <Chip
              label="Non-exchangeable"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.common.white, 0.1), 
                color: theme.palette.common.white, 
                opacity: 0.6 
              }}
            />
          )}

          {walletData.settings?.maxDailyTransferLimit > 0 && (
            <Tooltip title={`Maximum ${walletData.settings.maxDailyTransferLimit} tokens can be transferred per day`}>
              <Chip
                label={`Limit: ${walletData.settings.maxDailyTransferLimit}/day`}
                size="small"
                sx={{ 
                  bgcolor: alpha(theme.palette.common.white, 0.1), 
                  color: theme.palette.common.white,
                  fontWeight: 'medium'
                }}
              />
            </Tooltip>
          )}
          
          <IconButton 
            size="small" 
            sx={{ 
              ml: 'auto', 
              bgcolor: alpha(theme.palette.common.white, 0.1),
              color: theme.palette.common.white,
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.2),
              }
            }}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </GlassCard>
  );
};

export default TokenWalletCard;